import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, Download } from "lucide-react";

// ── In-app image viewer (Ink). Mount <ImageLightbox/> once near the app root.
//    Open from anywhere with: openImageLightbox(url, alt?)
//    Closes on Esc, backdrop click, or the × button — returns to the chat.

const EVENT = "open-image-lightbox";

export function openImageLightbox(src, alt = "") {
  if (!src) return;
  window.dispatchEvent(new CustomEvent(EVENT, { detail: { src, alt } }));
}

export default function ImageLightbox() {
  const [state, setState] = useState(null); // { src, alt } | null

  const close = useCallback(() => setState(null), []);

  useEffect(() => {
    const onOpen = (e) => setState(e.detail || null);
    window.addEventListener(EVENT, onOpen);
    return () => window.removeEventListener(EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (!state) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    // lock background scroll while the viewer is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [state, close]);

  if (!state) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100000] flex items-center justify-center bg-ink/90 backdrop-blur-sm animate-fade-in"
      onClick={close}
      role="dialog"
      aria-modal="true"
    >
      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 flex items-center justify-end gap-2 p-4">
        <a
          href={state.src}
          download
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="w-10 h-10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Download image"
          title="Download"
        >
          <Download size={18} />
        </a>
        <button
          onClick={close}
          className="w-10 h-10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Close viewer"
          title="Close (Esc)"
        >
          <X size={20} />
        </button>
      </div>

      {/* Image — stop propagation so clicking the image doesn't close */}
      <img
        src={state.src}
        alt={state.alt}
        onClick={(e) => e.stopPropagation()}
        className="max-w-[92vw] max-h-[88vh] object-contain border border-white/10 select-none animate-scale-in"
      />
    </div>,
    document.body,
  );
}
